/**
 * WizardStepper Component
 *
 * Displays a horizontal progress indicator for the 6-step device update wizard.
 * Shows current, completed, and pending steps with visual feedback.
 */

import * as React from 'react';
import styled from 'styled-components';
import { Icon } from '../../../shared/Icon';
import {
  WizardStep,
  StepState,
  WIZARD_STEPS_ORDER,
} from '../types/wizard-types';
import { useWizard } from '../hooks/useWizard';
import { KEYS } from '../../../../keys';

/**
 * Step configuration with metadata
 */
interface StepConfig {
  id: WizardStep;
  label: string;
  shortLabel: string;
  iconName: string;
}

/**
 * Step configurations for the wizard
 */
const STEP_CONFIGS: StepConfig[] = [
  {
    id: WizardStep.EID_ENTRY,
    label: 'Get Started',
    shortLabel: 'Start',
    iconName: 'play_arrow',
  },
  {
    id: WizardStep.DEVICE_CONNECTION,
    label: 'Connect Plates',
    shortLabel: 'Connect',
    iconName: 'usb',
  },
  {
    id: WizardStep.BOOTLOADER_TRANSFER,
    label: 'Transferring Update',
    shortLabel: 'Update',
    iconName: 'sync',
  },
  {
    id: WizardStep.EASY_INSTALLER_LOADING,
    label: 'Loading Update',
    shortLabel: 'Update',
    iconName: 'sync',
  },
  {
    id: WizardStep.FIRMWARE_FLASHING,
    label: 'Applying Update',
    shortLabel: 'Update',
    iconName: 'sync',
  },
  {
    id: WizardStep.FINALIZATION,
    label: 'Complete',
    shortLabel: 'Done',
    iconName: 'check_circle',
  },
];

/**
 * WizardStepper Props
 */
export interface WizardStepperProps {
  compact?: boolean;
}

/**
 * WizardStepper component
 */
export const WizardStepper: React.FC<WizardStepperProps> = ({
  compact = false,
}: WizardStepperProps): React.ReactElement => {
  const { state } = useWizard();
  const currentStepIndex = WIZARD_STEPS_ORDER.indexOf(state.currentStep);

  return (
    <StepperContainer $compact={compact} data-testid={KEYS.WIZARD_STEPPER}>
      {STEP_CONFIGS.map(
        (step: StepConfig, index: number): React.ReactElement => {
          const stepState = state.stepStates.get(step.id) || StepState.PENDING;
          const isCurrent = index === currentStepIndex;
          const isCompleted = stepState === StepState.COMPLETED;
          const isError = stepState === StepState.ERROR;
          const isInProgress = stepState === StepState.IN_PROGRESS;

          // Last step shows check icon, all other steps show their number
          const isLastStep = index === STEP_CONFIGS.length - 1;
          const stepNumber = index + 1;

          return (
            <React.Fragment key={step.id}>
              <StepItem
                $compact={compact}
                data-testid={`${KEYS.WIZARD_STEPPER_STEP}-${step.id}`}
              >
                <StepCircle
                  $isCurrent={isCurrent}
                  $isCompleted={isCompleted}
                  $isError={isError}
                  $isInProgress={isInProgress}
                  $compact={compact}
                >
                  {isCompleted ? (
                    <Icon
                      name='check'
                      color='white'
                      size={compact ? 'tiny' : 'small'}
                    />
                  ) : isError ? (
                    <Icon
                      name='error'
                      color='white'
                      size={compact ? 'tiny' : 'small'}
                    />
                  ) : isInProgress ? (
                    <SpinnerIcon $compact={compact}>
                      <Icon
                        name='sync'
                        color='white'
                        size={compact ? 'tiny' : 'small'}
                      />
                    </SpinnerIcon>
                  ) : isLastStep ? (
                    <Icon
                      name='check_circle'
                      color={isCurrent ? 'white' : undefined}
                      size={compact ? 'tiny' : isCurrent ? 'small' : 'tiny'}
                    />
                  ) : (
                    <StepNumber $isCurrent={isCurrent} $compact={compact}>
                      {stepNumber}
                    </StepNumber>
                  )}
                </StepCircle>
                <StepLabel
                  $isCurrent={isCurrent}
                  $isCompleted={isCompleted}
                  $isError={isError}
                  $compact={compact}
                >
                  {compact ? step.shortLabel : step.label}
                </StepLabel>
              </StepItem>
              {index < STEP_CONFIGS.length - 1 && (
                <ConnectorLine
                  $isCompleted={isCompleted}
                  $isError={isError && index === currentStepIndex}
                  $compact={compact}
                />
              )}
            </React.Fragment>
          );
        }
      )}
    </StepperContainer>
  );
};

// Styled Component Props

interface StepperContainerProps {
  $compact: boolean;
}

interface StepItemProps {
  $compact: boolean;
}

interface StepCircleProps {
  $isCurrent: boolean;
  $isCompleted: boolean;
  $isError: boolean;
  $isInProgress: boolean;
  $compact: boolean;
}

interface StepLabelProps {
  $isCurrent: boolean;
  $isCompleted: boolean;
  $isError: boolean;
  $compact: boolean;
}

interface ConnectorLineProps {
  $isCompleted: boolean;
  $isError: boolean;
  $compact: boolean;
}

interface StepNumberProps {
  $isCurrent: boolean;
  $compact: boolean;
}

// Styled Components

const StepperContainer = styled.div<StepperContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props: StepperContainerProps): string =>
    props.$compact ? '8px 4px' : '16px 12px'};
  width: 100%;
  background: var(--color-surface-1);
  border-radius: 10px;
  margin-bottom: 20px;
`;

const StepItem = styled.div<StepItemProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: ${(props: StepItemProps): string =>
    props.$compact ? '48px' : '70px'};
  cursor: default;
`;

const StepCircle = styled.div<StepCircleProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props: StepCircleProps): string => {
    if (props.$compact) return props.$isCurrent ? '36px' : '32px';
    return props.$isCurrent ? '48px' : '40px';
  }};
  height: ${(props: StepCircleProps): string => {
    if (props.$compact) return props.$isCurrent ? '36px' : '32px';
    return props.$isCurrent ? '48px' : '40px';
  }};
  border-radius: 50%;
  background-color: ${(props: StepCircleProps): string => {
    if (props.$isError) return 'var(--color-react-select-danger)';
    if (props.$isCurrent || props.$isInProgress) return 'var(--color-primary)';
    if (props.$isCompleted) return '#66bb6a';
    return 'rgba(158, 158, 158, 0.2)';
  }};
  border: 2px solid
    ${(props: StepCircleProps): string => {
      if (props.$isError) return 'var(--color-react-select-danger)';
      if (props.$isCurrent || props.$isInProgress) {
        return 'var(--color-primary)';
      }
      if (props.$isCompleted) return '#66bb6a';
      return 'rgba(158, 158, 158, 0.3)';
    }};
  transition: all 300ms ease-in-out;
  margin-bottom: ${(props: StepCircleProps): string =>
    props.$compact ? '4px' : '8px'};
`;

const StepLabel = styled.span<StepLabelProps>`
  font-size: ${(props: StepLabelProps): string => {
    if (props.$compact) return props.$isCurrent ? '9px' : '8px';
    return props.$isCurrent ? '12px' : '10px';
  }};
  font-weight: ${(props: StepLabelProps): string =>
    props.$isCurrent ? 'bold' : 'normal'};
  color: ${(props: StepLabelProps): string => {
    if (props.$isError) return 'var(--color-react-select-danger)';
    if (props.$isCurrent) return 'var(--color-primary)';
    if (props.$isCompleted) return '#66bb6a';
    return 'var(--color-disabled-text)';
  }};
  text-align: center;
  transition: all 300ms ease-in-out;
  white-space: nowrap;
`;

const ConnectorLine = styled.div<ConnectorLineProps>`
  flex: 1;
  height: 2px;
  min-width: ${(props: ConnectorLineProps): string =>
    props.$compact ? '12px' : '20px'};
  max-width: ${(props: ConnectorLineProps): string =>
    props.$compact ? '24px' : '40px'};
  background-color: ${(props: ConnectorLineProps): string => {
    if (props.$isError) return 'var(--color-react-select-danger)';
    if (props.$isCompleted) return '#66bb6a';
    return 'rgba(158, 158, 158, 0.3)';
  }};
  margin: 0
    ${(props: ConnectorLineProps): string => (props.$compact ? '2px' : '4px')}
    ${(props: ConnectorLineProps): string => (props.$compact ? '18px' : '28px')};
  transition: background-color 300ms ease-in-out;
`;

const SpinnerIcon = styled.div<{ $compact: boolean }>`
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  animation: spin 1s linear infinite;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepNumber = styled.span<StepNumberProps>`
  font-size: ${(props: StepNumberProps): string => {
    if (props.$compact) return props.$isCurrent ? '14px' : '12px';
    return props.$isCurrent ? '18px' : '14px';
  }};
  font-weight: 600;
  color: ${(props: StepNumberProps): string =>
    props.$isCurrent ? 'white' : 'var(--color-disabled-text)'};
`;

export default WizardStepper;
